import type { GetUser } from "twi-client";

import type { SubscriptionStatus } from "~/types/subscription";
import type { User } from "~/types/user";

import { DEFAULT_TZ } from "./constants";

type UserNameLike = {
  firstName?: string | null;
  lastName?: string | null;
};

export type MockUserForDevo = Pick<User, "id" | "hasDevoPlus" | "timezone">;

/**
 * Create a mock user object for content transformation
 * Used when user is unauthenticated but content needs to be displayed
 * Centralizes mock user creation logic (DRY + SRP)
 */
export function createMockUserForDevo(): MockUserForDevo {
  return {
    id: 0,
    hasDevoPlus: false,
    timezone: DEFAULT_TZ,
  };
}

/**
 * Get user for devo content transformation
 * Returns actual user if available, otherwise returns mock user
 */
export function getUserForDevo(user: User | null): User | MockUserForDevo {
  return user ?? createMockUserForDevo();
}

export function hasCompletedNameProfile(user: UserNameLike | null | undefined): boolean {
  const firstName = user?.firstName?.trim();

  return Boolean(firstName);
}

export function getUserStatus(user: User, subscription: SubscriptionStatus | null): string | null {
  if (!user.hasDevoPlus) {
    return "Free";
  }
  if (subscription) {
    if (subscription.trial?.isInTrial) {
      return "Free Trial";
    }
    return "Sing+";
  }
  return null;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: flat field-by-field normalization of API user fields.
export function normalizeUser(apiUser: GetUser): User {
  return {
    id: apiUser.id ?? 0,
    externalId: apiUser.externalId ?? "",
    firstName: apiUser.firstName ?? "",
    lastName: apiUser.lastName ?? "",
    phoneNumber: apiUser.phoneNumber ?? "",
    email: apiUser.email ?? null,
    preferredDailyChannel: apiUser.preferredDailyChannel === "Email" ? "email" : "sms",
    timezone: apiUser.timezone ?? DEFAULT_TZ,
    alwaysShowTranscript: apiUser.alwaysShowTranscript ?? false,
    hasDevoPlus: apiUser.hasDevoPlus ?? false,
    createdAtUtc: apiUser.createdAtUtc ?? "",
    lastLoginAtUtc: apiUser.lastLoginAtUtc ?? "",
    promotionalBannerDismissedAt: apiUser.promotionalBannerDismissedAt ?? null,
    optedOutViaApp: apiUser.optedOutViaApp ?? null,
    preferredSendTime: apiUser.preferredSendTime ?? null,
    doNotMessage: apiUser.doNotMessage ?? false,
    marketingMessageProvider: apiUser.marketingMessageProvider ?? "Pbt",
    smsMarketingStatus: apiUser.smsMarketingStatus ?? null,
    smsMarketingPreferenceUpdatedAtUtc: apiUser.smsMarketingPreferenceUpdatedAtUtc ?? null,
    languagePreference: apiUser.languagePreference ?? null,
    birthYear: apiUser.birthYear ?? null,
    birthMonth: apiUser.birthMonth ?? null,
    googlePlaceId: apiUser.googlePlaceId ?? null,
    addressName: apiUser.addressName ?? null,
    street1: apiUser.street1 ?? null,
    street2: apiUser.street2 ?? null,
    city: apiUser.city ?? null,
    region: apiUser.region ?? null,
    postalCode: apiUser.postalCode ?? null,
    country: apiUser.country ?? null,
    latitude: apiUser.latitude ?? null,
    longitude: apiUser.longitude ?? null,
  };
}
